/**
 * PVL Compare Two Rows Table Web Component
 * Updates feature rows from metaobject data while preserving existing table structure
 * Data format: { features: [{ label, values: { proh2o: {...}, competitor: {...} } }] }
 */
class PVLCompareTwoRowsTable extends HTMLElement {
  /**
   * Initialize component when inserted into DOM
   */
  connectedCallback() {
    // Extract data from attribute
    const dataAttr = this.getAttribute("data-table-rows");

    if (!dataAttr) {
      console.warn("[PVL Compare Table] No data-table-rows attribute found");
      return;
    }

    try {
      const parsedData = JSON.parse(dataAttr);

      // Delay rendering briefly to show skeleton
      requestAnimationFrame(() => {
        setTimeout(() => this.updateRows(parsedData), 150);
      });
    } catch (e) {
      console.error("[PVL Compare Table] Failed to parse data:", e);
    }
  }

  /**
   * Update feature rows from data (preserves existing structure)
   * @param {Object} data - Data object with features array
   */
  updateRows(data) {
    // Handle data.features array or direct array
    const features = data.features || data;

    if (!Array.isArray(features) || features.length === 0) {
      console.warn("[PVL Compare Table] No features found in data");
      return;
    }

    // Remove all skeleton placeholder rows
    const skeletonRows = this.querySelectorAll(".pvl-compare-skeleton__row");
    skeletonRows.forEach((row) => row.remove());

    // Update mobile rows for "our" product (proh2o)
    const mobileOurContainer = this.querySelector('[data-product-type="our"]');
    if (mobileOurContainer) {
      this.renderMobileRows(features, mobileOurContainer, "proh2o");
    }

    // Update mobile rows for competitor
    const mobileCompetitorContainer = this.querySelector(
      '[data-product-type="competitor"]',
    );
    if (mobileCompetitorContainer) {
      this.renderMobileRows(features, mobileCompetitorContainer, "competitor");
    }

    // Update desktop table rows
    const desktopTbody = this.querySelector(".pvl-compare__table-body");
    if (desktopTbody) {
      this.renderDesktopRows(features, desktopTbody);
    }

    // Mark as loaded
    this.setAttribute("data-loaded", "true");
  }

  /**
   * Render mobile feature rows
   * @param {Array} features - Features array
   * @param {Element} container - Container element
   * @param {String} productType - Product type ('proh2o' or 'competitor')
   */
  renderMobileRows(features, container, productType) {
    const modifier = productType === "proh2o" ? "primary" : "competitor";

    const rows = features
      .map((feature) => {
        const value = feature.values?.[productType];
        if (!value) return "";

        return `
        <div class="pvl-compare__feature-row">
          <dt class="pvl-compare__feature-label">${this.escapeHtml(feature.label || "")}</dt>
          <dd class="pvl-compare__feature-value">
            ${this.formatValue(value, modifier)}
          </dd>
        </div>
      `;
      })
      .join("");

    container.innerHTML = rows;
  }

  /**
   * Render desktop table rows
   * @param {Array} features - Features array
   * @param {Element} tbody - Table body element
   */
  renderDesktopRows(features, tbody) {
    const rows = features
      .map((feature) => {
        const proh2oValue = feature.values?.proh2o;
        const competitorValue = feature.values?.competitor;

        if (!proh2oValue || !competitorValue) return "";

        return `
        <tr class="pvl-compare__table-row">
          <td class="pvl-compare__table-cell pvl-compare__table-cell--label">${this.escapeHtml(feature.label || "")}</td>
          <td class="pvl-compare__table-cell pvl-compare__table-cell--center">
            ${this.formatValue(proh2oValue, "primary")}
          </td>
          <td class="pvl-compare__table-cell pvl-compare__table-cell--center">
            ${this.formatValue(competitorValue, "competitor")}
          </td>
        </tr>
      `;
      })
      .join("");

    tbody.innerHTML = rows;
  }

  /**
   * Format cell value based on type
   * @param {Object} valueObj - Value object with type, value, and optional properties
   * @param {String} modifier - Style modifier ('primary' or 'competitor')
   * @returns {String} Formatted HTML
   */
  formatValue(valueObj, modifier) {
    if (!valueObj || typeof valueObj !== "object") {
      return "";
    }

    const { type, value, bold, icon } = valueObj;

    // Handle icon type (checkmark or x)
    if (type === "icon") {
      return this.renderIcon(icon, value, modifier);
    }

    // Handle text type
    if (type === "text") {
      const boldClass = bold ? " pvl-compare__feature-text--bold" : "";
      return `<span class="pvl-compare__feature-text pvl-compare__feature-text--${modifier}${boldClass}">${this.escapeHtml(value)}</span>`;
    }

    return this.escapeHtml(value || "");
  }

  /**
   * Render icon (checkmark or x)
   * @param {String} iconType - Icon type ('checkmark' or 'x')
   * @param {String} srText - Screen reader text
   * @param {String} modifier - Style modifier ('primary' or 'competitor')
   * @returns {String} SVG icon HTML
   */
  renderIcon(iconType, srText, modifier) {
    const isCheckmark = iconType === "checkmark";

    // SVG paths for icons
    const path = isCheckmark
      ? "M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
      : "M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z";

    return `
      <svg class="pvl-compare__icon pvl-compare__icon--${modifier}" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
        <path ${isCheckmark ? 'fill-rule="evenodd"' : ""} d="${path}" ${isCheckmark ? 'clip-rule="evenodd"' : ""} />
      </svg>
      <span class="sr-only">${this.escapeHtml(srText || (isCheckmark ? "Yes" : "No"))}</span>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {String} str - String to escape
   * @returns {String} Escaped string
   */
  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

// Register custom element
customElements.define("pvl-compare-two-rows-table", PVLCompareTwoRowsTable);
